import { Filter } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEntryCalendarSummary } from "@/shared/cache";
import {
	DayHoursCell,
	WeekHoursCell,
} from "@/shared/components/entry-calendar/CalendarHourCells";
import {
	calendarSummaryToDaySums,
	monthDateFilter,
	visibleGridDateFilter,
	yearDateFilter,
} from "@/shared/components/entry-calendar/calendarData";
import { EntryCalendar } from "@/shared/components/entry-calendar/EntryCalendar";
import { useEntryViewState } from "@/shared/contexts/EntryViewStateContext";
import { Button } from "@/v2/components/ui/button";
import { toYYYYMMDD } from "./Entries/entryFilter";
import { entryFiltersToEntriesScope } from "./Entries/entryQueryScope";
import { isDefaultFilters } from "./Entries/filters/defaults";
import { FilterMenu } from "./Entries/filters/FilterMenu";
import { normalizeOptions } from "./Entries/filters/fields";
import { useFilterOptions } from "./Entries/filters/useFilterOptions";
import { useAttorneyFilters } from "./Entries/filters/useFilters";
import { StatsBar, statsTotalsFromSummary } from "./Entries/StatsBar";
import { useConfiguredTimekeepers } from "./Entries/timekeepers";

const EMPTY_FILTER_OPTION_ENTRIES: [] = [];

/** Attorney adapter for the shared entry calendar presentation. */
export default function Calendar2View() {
	const {
		date,
		dateRange,
		viewedMonth,
		setDate,
		setDateRange,
		setViewedMonth,
	} = useEntryViewState();
	const navigate = useNavigate();

	const goToEntriesForDay = useCallback(
		(day: Date) => {
			setDate(day);
			navigate("/entries");
		},
		[navigate, setDate],
	);
	const goToEntriesForWeek = useCallback(
		(weekStart: Date, weekEnd: Date) => {
			setDateRange({ from: weekStart, to: weekEnd });
			navigate("/entries");
		},
		[navigate, setDateRange],
	);

	const timekeepers = useConfiguredTimekeepers();
	const fieldOptions = useFilterOptions(
		EMPTY_FILTER_OPTION_ENTRIES,
		timekeepers,
	);
	const {
		filters,
		defaultFilters,
		toggleFieldValue,
		selectAll,
		selectNone,
		reset,
	} = useAttorneyFilters(fieldOptions);

	const statusValues = filters.status;
	const billableValues = useMemo(
		() =>
			filters.billableCategory ??
			normalizeOptions(fieldOptions.billableCategory ?? []).map(
				(option) => option.value,
			),
		[filters.billableCategory, fieldOptions.billableCategory],
	);

	const gridDateFilter = useMemo(
		() => visibleGridDateFilter(viewedMonth),
		[viewedMonth],
	);
	const yearFilter = useMemo(() => yearDateFilter(viewedMonth), [viewedMonth]);
	const gridSummaryScope = useMemo(
		() =>
			entryFiltersToEntriesScope(filters, gridDateFilter, {
				includeAttachments: false,
			}),
		[filters, gridDateFilter],
	);
	const yearSummaryScope = useMemo(
		() =>
			entryFiltersToEntriesScope(filters, yearFilter, {
				statuses: ["tracked", "reviewed", "exported"],
				includeFieldFilters: false,
				includeAttachments: false,
			}),
		[filters, yearFilter],
	);
	const { data: gridSummaryDays, isLoading: isGridLoading } =
		useEntryCalendarSummary(gridSummaryScope);
	const { data: yearSummaryDays, isLoading: isYearLoading } =
		useEntryCalendarSummary(yearSummaryScope);
	const sumsByDay = useMemo(
		() => calendarSummaryToDaySums(gridSummaryDays),
		[gridSummaryDays],
	);

	const monthFilter = useMemo(
		() => monthDateFilter(viewedMonth),
		[viewedMonth],
	);
	const statsTotals = useMemo(
		() => statsTotalsFromSummary(yearSummaryDays, monthFilter),
		[yearSummaryDays, monthFilter],
	);
	const hasActiveFilters = !isDefaultFilters(filters, defaultFilters);

	return (
		<EntryCalendar
			selectedDate={date}
			dateRange={dateRange}
			viewedMonth={viewedMonth}
			yearSummaryDays={yearSummaryDays}
			onSelectMonth={setViewedMonth}
			onDayClick={goToEntriesForDay}
			onWeekClick={goToEntriesForWeek}
			isLoading={isGridLoading || isYearLoading}
			filterSlot={
				<FilterMenu
					fieldOptions={fieldOptions}
					filters={filters}
					defaultFilters={defaultFilters}
					onToggleFieldValue={toggleFieldValue}
					onSelectAll={selectAll}
					onSelectNone={selectNone}
					onReset={reset}
					trigger={
						<Button
							variant="outline"
							size="icon"
							aria-label="Filter"
							className="relative h-11 w-11 shrink-0 self-center"
						>
							<Filter />
							{hasActiveFilters && (
								<span className="absolute top-1 right-1 size-1.5 rounded-full bg-primary" />
							)}
						</Button>
					}
				/>
			}
			renderDay={(context) => (
				<DayHoursCell
					ctx={context}
					sums={sumsByDay.get(toYYYYMMDD(context.date))}
					statusValues={statusValues}
					billableValues={billableValues}
				/>
			)}
			renderWeek={(context) => (
				<WeekHoursCell
					ctx={context}
					sumsByDay={sumsByDay}
					statusValues={statusValues}
					billableValues={billableValues}
				/>
			)}
			footerSlot={<StatsBar totals={statsTotals} />}
		/>
	);
}
